import { Stack, Typography, useTheme } from '@mui/material';
import { memo, useMemo, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DynamicIcon, PlatformUserAvatar, ShortcutText } from 'src/components';
import { SelectMenu, SelectMenuOption } from 'src/components/SelectMenu';
import { appShortcuts } from 'src/constants/appShortcuts';
import { componentsFontSizes } from 'src/constants/layout';
import { ConversationAssigneeOption } from 'src/graphql/entities/platform-user';
import { useRegisterShortcuts } from 'src/utils/hooks';

const AssigneeAvatar = ({ option }: { option: ConversationAssigneeOption }) => {
  const theme = useTheme();
  return (
    <PlatformUserAvatar
      name={option.userName}
      partnerId={option.partnerId}
      avatarFileKey={option.avatarFileKey}
      sx={{
        width: '1.25rem',
        height: '1.25rem',
        fontSize: theme.utils.important(componentsFontSizes.coloredAvatar),
      }}
    />
  );
};

const AssigneeTriggerLines = ({ name, group }: { name: string; group: string }) => (
  <Stack component="span" minWidth={0}>
    <Typography variant="inherit" component="span" noWrap lineHeight="1.125rem">
      {name}
    </Typography>
    <Typography
      variant="inherit"
      component="span"
      noWrap
      sx={(theme) => ({
        fontSize: '0.6875rem',
        lineHeight: '0.875rem',
        color: theme.palette.custom.OV.sys.onSurfaceVariant,
      })}
    >
      {group}
    </Typography>
  </Stack>
);

const ConversationAssigneeToggleComponent = ({
  name,
  options,
  disabled,
}: {
  name: string;
  options: ConversationAssigneeOption[];
  disabled?: boolean;
}) => {
  const { control } = useFormContext();
  const triggerRef = useRef<HTMLButtonElement>(null);

  useRegisterShortcuts(
    useMemo(
      () => ({
        ...appShortcuts.changeAssignee,
        callback: () => {
          // Bail when the trigger has focus (SelectMenu handles the key itself)
          // and while the menu is open, so clicking the trigger below can never
          // toggle an open menu shut.
          if (
            disabled ||
            document.activeElement === triggerRef.current ||
            triggerRef.current?.getAttribute('aria-expanded') === 'true'
          ) {
            return false;
          }
          triggerRef.current?.focus();
          triggerRef.current?.click();
          return false;
        },
      }),
      [disabled],
    ),
  );

  // `useConversationAssigneeOptions` already sorts by group, so each group's rows
  // are consecutive and `sectioned` renders one heading each. Inactive users stay
  // out of the list, as the old list item did by rendering null for them.
  const menuOptions = useMemo<SelectMenuOption[]>(
    () =>
      options
        .filter((option) => option.isActive)
        .map((option) => ({
          // `value` on the source option is the group id, which every row in a
          // group shares; `id` is unique (`groupId` for the group row itself,
          // `groupId_userId` for its users).
          value: option.id,
          title: option.userName,
          group: option.group,
          icon: option.userId ? (
            <AssigneeAvatar option={option} />
          ) : (
            <DynamicIcon name="users" library="lucide" />
          ),
        })),
    [options],
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange } }) => {
        const current = value as ConversationAssigneeOption | null;

        return (
          <SelectMenu
            ref={triggerRef}
            label="Assignee"
            options={menuOptions}
            value={current?.id ?? null}
            onChange={(selectedId) => {
              // Hand back the whole option so the group + user pairing survives.
              const selected = options.find((option) => option.id === selectedId);
              if (selected) {
                onChange(selected);
              }
            }}
            // The selected assignee can be an inactive user filtered out of the
            // rows, so the trigger renders from the form value.
            triggerLabel={current?.label}
            triggerContent={
              current?.userLabel ? (
                <AssigneeTriggerLines name={current.userLabel} group={current.group} />
              ) : undefined
            }
            triggerIcon={
              current?.userId ? (
                <AssigneeAvatar option={current} />
              ) : (
                <DynamicIcon name="users" library="lucide" />
              )
            }
            placeholder="Assign"
            hotkey={appShortcuts.changeAssignee.command}
            variant="inline"
            sectioned
            plainOptionIcons
            disabled={disabled}
            menuWidth="21.875rem"
            sx={{ width: '100%' }}
            tooltip={
              <ShortcutText
                label={appShortcuts.changeAssignee.description}
                shortcut={appShortcuts.changeAssignee.command}
                size="small"
              />
            }
          />
        );
      }}
    />
  );
};

export const ConversationAssigneeToggle = memo(ConversationAssigneeToggleComponent);
